// App.jsx
import React, { useEffect, useState } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import {
  getCurrentUser,
  signOut,
  updateMFAPreference,
  fetchMFAPreference,
  setUpTOTP,
  verifyTOTPSetup,
  rememberDevice,
  fetchDevices,
  forgetDevice
} from 'aws-amplify/auth';
import { CookieStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import '@aws-amplify/ui-react/styles.css';
import { QRCodeSVG } from 'qrcode.react';
import { components, formFields } from './components/layout/CustomStyles';
import Sidebar from './components/layout/Sidebar';
import UserTab from './components/layout/UserTab';
import Footer from './components/layout/Footer';
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import AppRoutes from './routes';
import Questionnaire from './components/Questionnaire';

const cookieStorage = new CookieStorage({
  domain: window.location.hostname,
  path: '/',
  expires: 30,
  secure: true,
});

cognitoUserPoolsTokenProvider.setKeyValueStorage(cookieStorage);

function App() {
  const [userDetails, setUserDetails] = useState({ firstName: '', lastNameInitial: '' });
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showTotpSetup, setShowTotpSetup] = useState(false);
  const [rememberThisDevice, setRememberThisDevice] = useState(false);
  const [totpUri, setTotpUri] = useState('');
  const [sharedSecret, setSharedSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const idleTimeoutMinutes = mfaEnabled ? 60 : 15;

  const handleTOTPSetup = async () => {
    try {
      setLoading(true);
      const details = await setUpTOTP();
      const appName = 'The Health Collaborative';
      const uri = details.getSetupUri(appName);
      setTotpUri(uri);
      setSharedSecret(details.sharedSecret);
      setShowTotpSetup(true);
    } catch (err) {
      console.error('Error setting up TOTP:', err);
      alert('Failed to start TOTP setup.');
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPVerify = async () => {
    try {
      setLoading(true);
      await verifyTOTPSetup({ code: totpCode });
      await updateMFAPreference({ totp: 'PREFERRED' });

      if (rememberThisDevice) {
        await rememberDevice();
        localStorage.setItem('deviceRemembered', 'true');
        localStorage.setItem('deviceRememberedAt', Date.now().toString());
      } else {
        // ✅ Ensure we still track when MFA was completed (for timeout later)
        localStorage.setItem('deviceRememberedAt', Date.now().toString());
      }

      setMfaEnabled(true);
      setShowTotpSetup(false);
      setTotpCode('');
      setRememberThisDevice(false);

      alert('MFA is now enabled' + (rememberThisDevice ? ' and this device is remembered!' : '!'));
    } catch (err) {
      console.error('Verification failed:', err);
      alert('Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const updateLastActivity = () => localStorage.setItem('lastActivity', Date.now());
    updateLastActivity();
    ['mousemove', 'keypress', 'click', 'scroll'].forEach(evt =>
      window.addEventListener(evt, updateLastActivity)
    );
    return () => {
      ['mousemove', 'keypress', 'click', 'scroll'].forEach(evt =>
        window.removeEventListener(evt, updateLastActivity)
      );
    };
  }, []);

  const checkAndForgetDevice = async () => {
    const rememberedAtStr = localStorage.getItem('deviceRememberedAt');
    const rememberedFlag = localStorage.getItem('deviceRemembered') === 'true';

    if (!rememberedFlag || !rememberedAtStr) return;

    const rememberedAt = Number(rememberedAtStr);
    const now = Date.now();
    const hoursElapsed = (now - rememberedAt) / (1000 * 60 * 60);

    if (hoursElapsed >= 24) {
      try {
        console.log('[MFA] 24 hours passed, forgetting device...');
        await forgetDevice();

        // Reset MFA tracking
        localStorage.removeItem('deviceRemembered');
        localStorage.removeItem('deviceRememberedAt');

        // Force logout to trigger MFA again on next login
        await signOut();
      } catch (err) {
        console.error('[MFA] Error forgetting device:', err);
      }
    }
  };

  useEffect(() => {
    const intervalId = setInterval(async () => {
      const lastActivity = Number(localStorage.getItem('lastActivity'));
      const now = Date.now();
      const diffMinutes = (now - lastActivity) / 60000;

      if (location.pathname !== '/questionnaire' && diffMinutes >= idleTimeoutMinutes) {
        alert('You have been idle for too long. Please log in again.');
        signOut();
        localStorage.setItem('signOut', Date.now());
        return;
      }

      await checkAndForgetDevice(); // 🔁 this will evaluate and act accordingly

    }, 60000);

    return () => clearInterval(intervalId);
  }, [location.pathname, idleTimeoutMinutes]);


  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'signOut') signOut();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const fetchMfaStatus = async () => {
    try {
      const mfaPref = await fetchMFAPreference();
      setMfaEnabled(mfaPref.preferred === 'TOTP');
    } catch (error) {
      console.log('Error fetching MFA status', error);
    }
  };

  const fetchUserDetails = async () => {
    const output = await fetchDevices();
    // console.log('output', output);
    try {
      const { signInDetails } = await getCurrentUser();
      const [firstName, lastNameWithDomain] = signInDetails.loginId.split('.');
      const firstNameCapitalized = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
      const lastNameInitial = lastNameWithDomain.charAt(0).toUpperCase();
      setUserDetails({ firstName: firstNameCapitalized, lastNameInitial });
    } catch (error) {
      console.log('User not authenticated', error);
    }
  };

  const isQuestionnaireRoute = location.pathname === '/questionnaire';

  return (
    <>
      {isQuestionnaireRoute ? (
        <Routes>
          <Route path="/questionnaire" element={<Questionnaire />} />
        </Routes>
      ) : (
        <Authenticator components={components} formFields={formFields} variation="modal" hideSignUp>
          {({ user }) => {
            if (user && !currentUser) {
              setCurrentUser(user);
              fetchMfaStatus();
              fetchUserDetails();
              // ✅ Initialize remembered timestamp if it's missing
              const remembered = localStorage.getItem('deviceRemembered');
              const rememberedAt = localStorage.getItem('deviceRememberedAt');

              if (remembered !== 'false' && !rememberedAt) {
                localStorage.setItem('deviceRemembered', 'true');
                localStorage.setItem('deviceRememberedAt', Date.now().toString());
              }
            }

            return (
              <div>
                <Sidebar mfaEnabled={mfaEnabled} handleTOTPSetup={handleTOTPSetup} />
                <UserTab />
                {showTotpSetup && (
                  <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1000,
                    transition: 'opacity 0.3s ease'
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '2rem',
                      borderRadius: '12px',
                      boxShadow: '0 0 20px rgba(0,0,0,0.2)',
                      textAlign: 'center',
                      maxWidth: '360px',
                      width: '100%',
                      animation: 'fadeIn 0.4s ease'
                    }}>
                      <h3>Set up MFA</h3>
                      <QRCodeSVG value={totpUri} size={200} />
                      <p style={{ fontSize: '0.8rem', wordWrap: 'break-word' }}>
                        Or enter manually: <strong>{sharedSecret}</strong>
                      </p>
                      <input
                        placeholder="Enter 6-digit code"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                        style={{ margin: '1rem 0', padding: '0.5rem', width: '80%' }}
                      />
                      <div>
                        <button onClick={handleTOTPVerify} disabled={loading} style={{ marginRight: '0.5rem' }}>
                          {loading ? 'Verifying...' : 'Verify'}
                        </button>
                        <button onClick={() => setShowTotpSetup(false)} disabled={loading}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/*" element={<AppRoutes />} />
                </Routes>
                <Footer />
              </div>
            );
          }}
        </Authenticator>
      )}
    </>
  );
}

export default App;
